import { useLang } from 'hooks/lang';

import { actions, ProjectInvoiceControl, invoiceControlStatusConfig } from 'entities';

import { Column, Row, LabeledInput, Modal, Button, LabeledTextarea } from 'components/Blocks';
import { ButtonGroup } from 'components/ButtonGroup';
import { CircleXIcon, HandCoinsIcon, SaveIcon } from 'components/Icons';

import { useToggleV2, useFormControl, useUser } from 'hooks';
import { useConfirmation } from 'providers/Confirmation';
import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';

interface State {
  method: 'PACKAGE' | 'STANDARD';
  config: {
    comment: string;
    installments?: number;
  };
}

export function SelectPaymentButton() {
  const user = useUser();
  const { t } = useLang();
  const [open, modalHandlers] = useToggleV2();

  const { state, dispatch } = useFormControl<State>({
    method: '' as State['method'],
    config: {
      comment: '',
    },
  });

  const { requestConfirmation } = useConfirmation();

  const {
    project: { invoice, id },
    updateProject,
  } = useProjectProfile();

  const changeMethod = (method: State['method']): void => {
    dispatch({
      method,
      config: ({ config }) => ({
        comment: config.comment,
        ...(method === 'STANDARD' ? { installments: config.installments ?? 1 } : {}),
      }),
    });
  };

  const setMethod = actions.invoice.selectProjectPaymentMethod.use({
    onSuccess: () => {
      updateProject((old) => ({
        ...old,

        invoice: {
          ...old.invoice,

          type: state.method as ProjectInvoiceControl['type'],

          typeConfig: state.config,

          status: old.invoice.readyToInvoice
            ? invoiceControlStatusConfig.readyToInvoice.code
            : old.invoice.status,
        },
      }));

      modalHandlers.turnOff();
    },
  });

  const executeChoice = (): void => {
    if (!state.method) return;

    requestConfirmation({
      size: 'small',

      title: t('select-method', { method: state.method }) as string,

      text: t('please-be-sure-client-has-confirmed'),

      type: 'irreversible',

      onConfirm: async () => {
        await setMethod.execute({
          project: id,

          ...state,
        });
      },
    });
  };

  const canSelect = [
    [invoice.owner.id, invoice.subOwner?.id].some((id) => id === user.id),
    !invoice.type,
  ].every(Boolean);

  if (!canSelect) return null;

  return (
    <>
      <Modal isOpen={open} close={modalHandlers.turnOff} title={t('define-payment-method')}>
        <p className="text-center text-sm">{t('select-one-of-the-payment-options-below')}</p>

        <Row justifyContent="center">
          <ButtonGroup
            value={state.method}
            onChange={changeMethod}
            options={[
              { label: t('standard'), value: 'STANDARD' },
              { label: t('package'), value: 'PACKAGE' },
            ]}
          />
        </Row>

        {!!state.method && (
          <Column>
            <LabeledTextarea
              label={t('comments')}
              value={state.config.comment}
              placeholder={t('type-here-a-comment')!}
              onChange={({ target }) =>
                dispatch({ config: ({ config }) => ({ ...config, comment: target.value }) })
              }
            />

            {state.method === 'STANDARD' && (
              <LabeledInput
                label={t('number-of-installments')}
                value={state.config.installments ?? 1}
                type="number"
                min={1}
                max={20}
                onChange={({ target }) =>
                  dispatch({
                    config: ({ config }) => ({
                      ...config,
                      installments: Number(target.value) || 1,
                    }),
                  })
                }
              />
            )}

            <Row justifyContent="space-between" gap="2">
              <Button variant="outlined" onClick={modalHandlers.turnOff}>
                <CircleXIcon />
                {t('cancel')}
              </Button>

              <Button onClick={executeChoice} disabled={!state.method} loading={setMethod.loading}>
                <SaveIcon />
                {t('save')}
              </Button>
            </Row>
          </Column>
        )}
      </Modal>

      <Button variant="outlined" color="border" onClick={modalHandlers.turnOn} className="mt-auto">
        {t('choose-method')}
        <HandCoinsIcon />
      </Button>
    </>
  );
}
